/* One place that talks to the API.
 *
 * The session is an httpOnly cookie, so `credentials: "include"` is the only
 * thing that carries it — there is no token in JavaScript to steal. A 401
 * means the session is gone; the auth layer listens for that and sends the
 * person back to the sign-in page rather than letting the board show stale
 * data it can no longer save. */

const BASE = import.meta.env.VITE_API_BASE || "/api";

const unauthorized = new Set();

export function onUnauthorized(fn) {
  unauthorized.add(fn);
  return () => unauthorized.delete(fn);
}

export class ApiError extends Error {
  constructor(status, code, message) {
    super(message || code || "Request failed");
    this.status = status;
    this.code = code;
  }
}

async function request(method, path, body, opts = {}) {
  let res;
  try {
    res = await fetch(BASE + path, {
      method,
      credentials: "include",
      headers: body === undefined ? {} : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    /* A failed fetch is the network, not the server: say so plainly rather
       than reporting it as a server error. */
    throw new ApiError(0, "offline", "Cannot reach the War Room server.");
  }

  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!res.ok) {
    /* `silent401` is for the one call that asks "am I signed in?" — a 401
       there is the answer, not a session that just expired. */
    if (res.status === 401 && !opts.silent401) {
      for (const fn of unauthorized) fn();
    }
    throw new ApiError(
      res.status,
      data?.error || "request_failed",
      data?.message || defaultMessage(res.status)
    );
  }
  return data;
}

function defaultMessage(status) {
  if (status === 401) return "Sign in to continue.";
  if (status === 403) return "You are not allowed to do that.";
  if (status === 404) return "That is not here.";
  if (status === 429) return "Too many attempts. Try again shortly.";
  return "The server refused that (" + status + ").";
}

export const api = {
  get: (p, o) => request("GET", p, undefined, o),
  post: (p, b, o) => request("POST", p, b ?? {}, o),
  patch: (p, b, o) => request("PATCH", p, b ?? {}, o),
  del: (p, o) => request("DELETE", p, undefined, o),
};

/* ── the calls, named for what they do ─────────────────────────────── */

export const authApi = {
  config: () => api.get("/auth/config"),
  me: () => api.get("/auth/me", { silent401: true }),
  /* The only way in. All the browser sends is Google's ID token; who that
     token belongs to is read from it by the server, never claimed here. */
  google: (credential) => api.post("/auth/google", { credential }),
  logout: () => api.post("/auth/logout"),
};

export const boardApi = {
  list: () => api.get("/playgrounds"),
  /* The id and idx are the server's to assign, so nothing is sent. */
  create: () => api.post("/playgrounds"),
  remove: (id) => api.del("/playgrounds/" + id),
  patch: (id, patch) => api.patch("/playgrounds/" + id, patch),
  logMeeting: (id) => api.post("/playgrounds/" + id + "/log-meeting"),
  setHealth: (id, manualHealth) => api.post("/playgrounds/" + id + "/health", { manualHealth }),
  kpiReviewed: (id, index, nextReviewOn) =>
    api.post("/playgrounds/" + id + "/kpis/" + index + "/reviewed", { nextReviewOn }),
  exportAll: () => api.get("/playgrounds/export/all"),
  import: (playgrounds) => api.post("/playgrounds/import", { playgrounds }),
  reset: () => api.post("/playgrounds/reset"),
};

export const peopleApi = {
  list: () => api.get("/users"),
  add: (person) => api.post("/users", person),
  update: (id, patch) => api.patch("/users/" + id, patch),
  remove: (id) => api.del("/users/" + id),
};

/* The access policy: which domains and addresses may sign in at all, as
   opposed to peopleApi, which is the people already on the list. */
export const accessRulesApi = {
  list: () => api.get("/access-rules"),
  add: (rule) => api.post("/access-rules", rule),
  update: (id, patch) => api.patch("/access-rules/" + id, patch),
  remove: (id) => api.del("/access-rules/" + id),
};

export const acksApi = {
  today: () => api.get("/acks/today"),
  acknowledge: () => api.post("/acks/today"),
  history: (days = 40) => api.get("/acks/history?days=" + days),
  /* Every day in a range, which is the only form that can answer "since
     this person joined" — the grid's window has no fixed length. */
  historyRange: (from, to) =>
    api.get("/acks/history?from=" + encodeURIComponent(from) + "&to=" + encodeURIComponent(to)),
  missedReviews: () => api.get("/acks/missed-reviews"),
};

export const notesApi = {
  config: () => api.get("/notes/config"),
  send: (note) => api.post("/notes", note),
  notify: (note) => api.post("/notes/notify", note),
};
